import React, { useEffect, useState } from 'react';
import { Settings, ChevronsLeft, ChevronsRight } from 'react-feather';
import SidebarSection from './SidebarSection';
import { theme } from './theme';
import './Sidebar.css';
import LogoutSection from './LogoutSection';
import { BRAND_COLORS, GRAY_COLORS } from '../globalTheme';
import { Typography } from '../Typography/Typography';
import { useAuth } from '@/lib/auth';

interface NavItem {
	name: string;
	link: string;
}

export interface SectionItem {
	name: string;
	icon?: React.ReactNode;
	items: NavItem[];
	maxHeight?: string;
	hasDropdown?: boolean;
}

export interface SectionGroup {
	title?: string;
	icon?: React.ReactNode;
	sections: SectionItem[];
}

export interface UserDetails {
	firstName: string;
	initials: string;
}

export interface SidebarProps {
	user: UserDetails;
	companyLogoURL?: string;
	sectionGroups: SectionGroup | SectionGroup[];
	settingsTitle: string;
	historyTitle?: string;
	searchTitle?: string;
	logoutTitle: string;
	supportTitle: string;
	customerSupportManagementSection: SectionItem;
	currentPath: string;
	openSearchModal?: React.Dispatch<React.SetStateAction<boolean>>;
	triggerCollapseToggle?: boolean;
}

export const MOBILE_BAR_HEIGHT = 64;

const Sidebar: React.FC<SidebarProps> = ({
	user,
	companyLogoURL = '',
	sectionGroups,
	settingsTitle,
	logoutTitle,
	supportTitle,
	customerSupportManagementSection,
	currentPath,
	triggerCollapseToggle
}) => {
	const [openSectionName, setOpenSectionName] = useState<string | null>(null);
	const [collapsed, setCollapsed] = useState(false);
	const { logout } = useAuth();

	useEffect(() => {
		if (triggerCollapseToggle) toggleSidebar();
	}, [triggerCollapseToggle]);

	const getDesktopStyles = () => ({
		...theme.sidebar,
		...(collapsed ? theme.collapsedSidebar : {})
	});

	const handleLogout = async () => {
		try {
			await logout();
		} catch (error) {
			console.error('Error during logout:', error);
		}
	};

	const toggleSidebar = () => {
		setCollapsed(!collapsed);
	};

	const handleSectionToggle = (name: string) => {
		if (!collapsed) {
			setOpenSectionName(openSectionName === name ? null : name);
		} else {
			setOpenSectionName(null);
		}
	};

	const renderProfilePicture = () => {
		if (!user) return null;

		return (
			<div
				className="flex items-center justify-center rounded-full text-center border"
				style={{
					width: '36px',
					height: '36px',
					backgroundColor: BRAND_COLORS.brand500,
					borderColor: BRAND_COLORS.brand900
				}}
			>
				<Typography size="md" color="white">
					{user.initials}
				</Typography>
			</div>
		);
	};

	const renderToggleButton = () => (
		<header
			style={{
				...theme.sidebarToggle,
				padding: '12px 5px 12px 10px',
				backgroundColor: 'transparent',
				zIndex: 999,
				borderBottom: `1px solid ${GRAY_COLORS.gray300}`
			}}
			className={`${collapsed ? 'collapsed-toggle-button' : 'toggle-button'} `}
			onClick={toggleSidebar}
			data-testid="sidebarHeader"
		>
			<>
				{!collapsed ? (
					<>
						<img
							src={'/logo-square.jpg'}
							alt="company-logo"
							style={{ objectFit: 'contain', borderRadius: '10px' }}
							width={'80px'}
							height={'80px'}
						/>
						<div data-testid="openedSidebarIcon">
							<ChevronsLeft size={20} />
						</div>
					</>
				) : (
					<div data-testid="closedSidebarIcon">
						<ChevronsRight size={20} />
					</div>
				)}
			</>
		</header>
	);

	const renderSectionContent = (sections: SectionItem[]) => (
		<ul>
			{sections.map((section, index) => (
				<SidebarSection
					key={index}
					hasDropdown={section.hasDropdown}
					maxHeight={section.maxHeight}
					currentPath={currentPath}
					icon={section.icon}
					title={section.name}
					items={section.items}
					collapsed={collapsed}
					isOpen={openSectionName === section.name}
					onToggle={() => handleSectionToggle(section.name)}
				/>
			))}
		</ul>
	);

	const renderMainContent = () => {
		if (Array.isArray(sectionGroups)) {
			return sectionGroups.map((group, index) => (
				<div key={index}>
					<div
						className={`${collapsed ? 'hidden md:flex' : 'flex'} flex-row gap-3 mt-1 ${
							collapsed ? 'items-center justify-center' : ''
						}`}
						style={{ padding: '10px' }}
					>
						{group.icon}
						{!collapsed && group.title && (
							<div className="flex flex-row gap-3">
								<Typography color={GRAY_COLORS.gray900} size="sm" weight="semibold">
									{group.title}
								</Typography>
							</div>
						)}
					</div>
					{renderSectionContent(group.sections)}
					<hr className="w-4/5 flex m-auto" />
				</div>
			));
		}
		return renderSectionContent(sectionGroups.sections);
	};

	const renderFooter = () => (
		<footer className="sidebar-footer">
			<SidebarSection
				icon={customerSupportManagementSection.icon}
				title={customerSupportManagementSection.name}
				currentPath={currentPath}
				hasDropdown={true}
				items={customerSupportManagementSection.items}
				collapsed={collapsed}
				isOpen={openSectionName === customerSupportManagementSection.name}
				onToggle={() => handleSectionToggle(customerSupportManagementSection.name)}
				isFixed
			/>
			<hr className="w-full flex m-auto mb-2" />
			<SidebarSection
				icon={<Settings size={16} />}
				title={settingsTitle}
				currentPath={currentPath}
				items={[{ name: 'Settings', link: 'settings' }]}
				collapsed={collapsed}
				isFixed
			/>
			<hr className="mt-2" />
			{user.firstName ? (
				<LogoutSection
					profilePicture={renderProfilePicture()}
					title={user.firstName}
					logoutTitle={logoutTitle}
					currentPath={currentPath}
					collapsed={collapsed}
					handleLogOut={handleLogout}
					supportTitle={supportTitle}
				/>
			) : (
				<div
					className="logout-section-loading animate-pulse"
					style={{
						height: '100%',
						width: '100%',
						maxHeight: companyLogoURL ? '56px' : '44px',
						overflow: 'hidden' as const,
						minWidth: '200px',
						backgroundColor: GRAY_COLORS.gray200
					}}
				/>
			)}
		</footer>
	);

	return (
		<div style={getDesktopStyles()} className="sidebar print:hidden">
			{renderToggleButton()}
			{renderMainContent()}
			{renderFooter()}
		</div>
	);
};

export default Sidebar;
